<script lang="ts" setup>
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
  import { usePDF } from '@/store/pdfStore'
  import Button from '../ui/button/Button.vue'
  import { useThemeColor } from '@/composables/useThemeColor'

  const pdf = usePDF()
  const themeColor = computed(() => useThemeColor().value)

  defineProps({
    showPrevNextButtons: {
      type: Boolean,
      required: false,
      default: false
    },
    showPageButtons: {
      type: Boolean,
      required: false,
      default: true
    }
  })

  const width = ref(window.innerWidth)
  const maxPagesToShow = ref(7)
  const currentPageSet = ref(1)

  const compactMode = computed(() => {
    return width.value < 768
  })

  const displayedPages = computed(() => {
    const startPage = (currentPageSet.value - 1) * maxPagesToShow.value + 1
    const endPage = Math.min(startPage + maxPagesToShow.value - 1, pdf.pages)
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)
  })

  const updateCurrentPageSet = (direction: string) => {
    if (direction === 'next' && pdf.page < pdf.pages) {
      pdf.gotoPage(pdf.page + 1)
      if (pdf.page > currentPageSet.value * maxPagesToShow.value) {
        currentPageSet.value++
      }
    } else if (direction === 'prev' && pdf.page > 1) {
      pdf.gotoPage(pdf.page - 1)
      if (pdf.page <= (currentPageSet.value - 1) * maxPagesToShow.value) {
        currentPageSet.value--
      }
    }
  }

  onMounted(() => {
    const updateWidth = () => (width.value = window.innerWidth)
    window.addEventListener('resize', updateWidth)
  })

  onUnmounted(() => {
    const updateWidth = () => (width.value = window.innerWidth)
    window.removeEventListener('resize', updateWidth)
  })
</script>

<template>
  <nav class="mb-1 flex justify-end">
    <ul
      v-show="pdf.pages > 1"
      class="flex list-none"
    >
      <li class="page-item mr-1">
        <Button
          variant="ghost"
          size="icon"
          data-cy="pdfNavPrevBtn"
          :disabled="pdf.page === 1"
          @click="updateCurrentPageSet('prev')"
        >
          <ChevronLeft class="h-4 w-4" />
        </Button>
      </li>
      <li
        v-for="page in displayedPages"
        :key="page"
        class="mr-1 text-center"
      >
        <Button
          size="icon"
          :class="
            page == pdf.page
              ? `bg-${themeColor}-600 hover:bg-${themeColor}-800'`
              : 'bg-gray-300 hover:bg-gray-500'
          "
          @click="pdf.gotoPage(page)"
        >
          {{ page }}
        </Button>
      </li>
      <li class="page-item">
        <Button
          variant="ghost"
          size="icon"
          data-cy="pdfNavNextBtn"
          :disabled="pdf.page === pdf.pages"
          @click="updateCurrentPageSet('next')"
        >
          <ChevronRight class="h-4 w-4" />
        </Button>
      </li>
    </ul>
  </nav>
</template>
